/**
 * Which optional columns of the document table fit the width it actually has.
 *
 * The rows are a CSS grid whose columns have fixed floors (DocumentListHeader /
 * DocumentTableRow / FolderTableRow share them), so a table narrower than the
 * sum of those floors scrolls sideways. Horizontal scrollbars are never
 * acceptable, so the table drops optional columns instead — least useful first:
 * Parent and Status, then Uploader, then Version, then Tags, and the date last:
 * with the meta line under the filename gone it is the only place a row says
 * when (and, once Uploader is dropped, its tooltip says who), but a readable
 * filename matters more. If even the checkbox, name and actions do not fit, the
 * name's floor gives way.
 */
export interface DocumentColumnFlags {
  showCheckbox: boolean;
  showDateCreated: boolean;
  showUser: boolean;
  showVersion: boolean;
  showStatus: boolean;
  showTags: boolean;
  showParent: boolean;
  showActions: boolean;
}

export type OptionalColumn =
  | "showParent"
  | "showStatus"
  | "showUser"
  | "showVersion"
  | "showTags"
  | "showDateCreated";

/** Floors in px, matching the grid templates in the row and header components. */
const FLOOR: Record<keyof DocumentColumnFlags, number> = {
  showCheckbox: 28,
  showDateCreated: 120,
  showUser: 72,
  showVersion: 80,
  showStatus: 100,
  showTags: 180,
  showParent: 150,
  showActions: 50,
};

const DROP_ORDER: OptionalColumn[] = [
  "showParent",
  "showStatus",
  "showUser",
  "showVersion",
  "showTags",
  "showDateCreated",
];

/** The rows' `px-6` and `gap-4`, in rem — the app's root size is not always 16px. */
const ROW_PADDING_REM = 3;
const COLUMN_GAP_REM = 1;

export const DEFAULT_NAME_MIN_WIDTH = 240;

/**
 * The rows' and header's `grid-template-columns`, from the same floors the fit
 * uses — one definition, so the header, document, folder and container rows
 * cannot drift apart. The name lowers its floor through
 * `--document-name-min-width` when even the fixed columns barely fit; it and
 * Tags share the slack, the filename taking the dominant share.
 */
export function documentGridTemplate(flags: DocumentColumnFlags): string {
  return [
    flags.showCheckbox && `${FLOOR.showCheckbox}px`,
    `minmax(var(--document-name-min-width, ${DEFAULT_NAME_MIN_WIDTH}px), 2.5fr)`,
    flags.showDateCreated && `${FLOOR.showDateCreated}px`,
    flags.showUser && `${FLOOR.showUser}px`,
    flags.showVersion && `${FLOOR.showVersion}px`,
    flags.showStatus && `${FLOOR.showStatus}px`,
    flags.showTags && `minmax(${FLOOR.showTags}px, 1.25fr)`,
    flags.showParent && `${FLOOR.showParent}px`,
    flags.showActions && `${FLOOR.showActions}px`,
  ]
    .filter(Boolean)
    .join(" ");
}

/** The narrowest width a row with these columns can take without overflowing. */
export function minimumRowWidth(
  flags: DocumentColumnFlags,
  nameMinWidth: number,
  remPx = 16
): number {
  const shown = (Object.keys(FLOOR) as Array<keyof DocumentColumnFlags>).filter((k) => flags[k]);
  const fixed = shown.reduce((sum, key) => sum + FLOOR[key], 0);
  // The name column is always there, so the columns number shown + 1.
  return (ROW_PADDING_REM + COLUMN_GAP_REM * shown.length) * remPx + nameMinWidth + fixed;
}

export interface FittedColumns {
  flags: DocumentColumnFlags;
  /** Set only when the name's floor itself had to give way. */
  nameMinWidth?: number;
}

/**
 * `available` is the rows' width in px; 0 or less means "not measured yet", and
 * the requested columns are kept as they are. `remPx` is the root font size.
 */
export function fitDocumentColumns(
  requested: DocumentColumnFlags,
  available: number,
  nameMinWidth: number = DEFAULT_NAME_MIN_WIDTH,
  remPx = 16,
  /**
   * Dropped last — the column the list is sorted by, so its arrow stays on
   * screen. It still goes before the filename's floor gives way.
   */
  keep: OptionalColumn | null = null
): FittedColumns {
  if (!(available > 0)) return { flags: requested };
  const flags = { ...requested };
  const order = keep ? [...DROP_ORDER.filter((c) => c !== keep), keep] : DROP_ORDER;
  for (const column of order) {
    if (minimumRowWidth(flags, nameMinWidth, remPx) <= available) return { flags };
    flags[column] = false;
  }
  const rest = minimumRowWidth(flags, 0, remPx);
  if (rest + nameMinWidth <= available) return { flags };
  return { flags, nameMinWidth: Math.max(0, Math.floor(available - rest)) };
}
